package gr.tech.GRAttestation.sections.document

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.FontStyle
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.autoLength
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.layout.HorizontalDivider
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import gr.tech.GRAttestation.infrastructure.session.StoreUserSession
import gr.tech.GRAttestation.models.attestation.AttestationForm
import gr.tech.GRAttestation.models.stores.StorePreviewDTO
import gr.tech.GRAttestation.sections.forms.formDefaultText
import gr.tech.GRAttestation.styles.*
import gr.tech.GRAttestation.util.Constants
import gr.tech.GRAttestation.util.formatCurrency
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.*

@Composable
fun AttestationDocument(
    formData: AttestationForm,
    storeData: StorePreviewDTO,
) {
    val dataRowsSize = formData.salesDataList.size
    val salesDataRowMax = Constants.FORM.SALES_DATA_LIMIT

    Div(Modifier.backgroundColor(Colors.White).toAttrs {
        id("attestation-form")
    }) {

        // Page 1
        Div(pageStyle.toModifier().toAttrs()) {
            AttestationPageNumber(1, 2)

            Img(
                src = "grlogocenter.png",
                alt = "Green Rebates Logo",
                attrs = GenHeaderLogoStyle.toModifier().margin(leftRight = autoLength, bottom = 24.px).toAttrs()
            )

            H1(
                attrs = GenHeaderStyle.toModifier().margin(bottom = 24.px)
                    .toAttrs {}
            ) { Text("Store Rebate Sales Reporting & Attestation Form") }

            P(attrs = GenDefaultText.toAttrs()) {
                SpanText("Program: ", Modifier.fontWeight(FontWeight.Bold))
                Text("Energy Trust of Oregon (ETO) Grow Light Fixture Program 2026")
            }

            P(attrs = GenDefaultText.toAttrs()) {
                SpanText("Partner: ", Modifier.fontWeight(FontWeight.Bold))
                Text("Green Rebates LLC")
            }

            P(attrs = GenDefaultText.toAttrs()) {
                SpanText("Effective Period: ", Modifier.fontWeight(FontWeight.Bold))
                Text("${Constants.FORM.EFF_START_DATE} - ${Constants.FORM.EFF_END_DATE}")
            }

            HorizontalDivider(GenHorizontalDivider.toModifier().margin(topBottom = 16.px))

            P(attrs = GenDefaultText.toAttrs()) {
                SpanText("Store Name: ", Modifier.margin(right = 28.px).fontWeight(FontWeight.Bold))
                Text(storeData.storeName)
            }

            P(attrs = GenDefaultText.toAttrs()) {
                SpanText("Location ID: ", Modifier.margin(right = 28.px).fontWeight(FontWeight.Bold))
                Text(storeData.locationId)
            }

            P(attrs = GenDefaultText.toAttrs()) {
                SpanText("Report Month: ", Modifier.margin(right = 12.px).fontWeight(FontWeight.Bold))
                Text(formData.reportingMonth)
            }

            HorizontalDivider(GenHorizontalDivider.toModifier().margin(topBottom = 16.px))

            P(attrs = GenDefaultText.fontWeight(FontWeight.Bold).margin(bottom = 8.px).toAttrs()) {
                Text("Sales Data Summary")
            }

            P(attrs = GenDefaultText.toAttrs()) {
                Text("The following information must be submitted for all qualifying sales under this program:")
            }

            Table(
                attrs = Modifier
                    .border(1.px, LineStyle.Solid, Colors.Black)
                    .margin(topBottom = 8.px)
                    .fillMaxWidth()
                    .toAttrs{
                        style {
                            property("border-collapse", "collapse")
                            property("table-layout", "fixed")
                        }
                    }
            )
            {
                Tr {
                    Th(GenTableColumnHeader.toModifier().width(150.px).toAttrs()) {
                        Text("SKU / Model #")
                    }
                    Th(GenTableColumnHeader.toModifier().width(190.px).toAttrs()) {
                        Text("Product Description")
                    }
                    Th(GenTableColumnHeader.toModifier().width(50.px).toAttrs()) {
                        Text("Qty")
                    }
                    Th(GenTableColumnHeader.toModifier().width(100.px).toAttrs()) {
                        Text("Rebate Per Unit")
                    }
                    Th(GenTableColumnHeader.toModifier().width(100.px).toAttrs()) {
                        Text("Total")
                    }
                }

                var runningTotal = 0L
                formData.salesDataList.forEachIndexed { index, item ->
                    runningTotal += item.skuModel.unitPrice * item.quantity
                    Tr(attrs = Modifier.toAttrs()) {
                        Td(attrs = GenTableColumn.toModifier().toAttrs()) {
                            Text(item.skuModel.sku)
                        }
                        Td(attrs = GenTableColumn.toModifier().toAttrs()) {
                            Text(item.skuModel.description)
                        }
                        Td(attrs = GenTableColumn.toModifier().toAttrs()) {
                            Text(item.quantity.toString())
                        }
                        Td(attrs = GenTableColumn.toModifier().toAttrs()) {
                            Text(formatCurrency(item.skuModel.unitPrice))
                        }
                        Td(attrs = GenTableColumn.toModifier().toAttrs()) {
                            Text(formatCurrency(runningTotal))
                        }
                    }
                }

//                repeat(maxOf(0, salesDataRowMax - dataRowsSize)) {
//                    Tr(attrs = Modifier.height(35.5.px).toAttrs()) {
//                        Td(attrs = GenTableColumn.toModifier().toAttrs()) { Text(" ") }
//                        Td(attrs = GenTableColumn.toModifier().toAttrs()) { Text(" ") }
//                        Td(attrs = GenTableColumn.toModifier().toAttrs()) { Text(" ") }
//                        Td(attrs = GenTableColumn.toModifier().toAttrs()) { Text(" ") }
//                        Td(attrs = GenTableColumn.toModifier().toAttrs()) { Text(" ") }
//                    }
//                }

                // Grand total row
                val totalQuantity = formData.salesDataList.sumOf { it.quantity }
                Tr(attrs = Modifier.toAttrs()) {
                    Td(attrs = GenTableColumn.toModifier().toAttrs()) { Text("Total:") }
                    Td(attrs = GenTableColumn.toModifier().toAttrs()) { Text(" ") }
                    Td(attrs = GenTableColumn.toModifier().fontWeight(FontWeight.Bold).toAttrs()) {
                        Text(totalQuantity.toString())
                    }
                    Td(attrs = GenTableColumn.toModifier().toAttrs()) { Text(" ") }
                    Td(attrs = GenTableColumn.toModifier().fontWeight(FontWeight.Bold).toAttrs()) {
                        Text(formatCurrency(runningTotal))
                    }
                }
            }
        }

        // Page 2
        Div(pageStyle.toModifier().toAttrs()) {
            AttestationPageNumber(2, 2)

            Img(
                src = "grlogocenter.png",
                alt = "Green Rebates Logo",
                attrs = GenHeaderLogoStyle.toModifier().margin(leftRight = autoLength, bottom = 24.px).toAttrs()
            )

            AttestationStatementList(display = true)

            P(attrs = formDefaultText.margin(bottom = 16.px).toAttrs()) {
                Text("Failure to comply with these requirements may result in the rejection of rebate claims or removal from the program participation.")
            }

            HorizontalDivider(GenHorizontalDivider.toModifier().margin(topBottom = 16.px))

            P(attrs = GenDefaultText.fontWeight(FontWeight.Bold).margin(bottom = 8.px).toAttrs()) {
                Text("Store Representative")
            }

            P(attrs = GenDefaultText.margin(bottom = 6.px).toAttrs()) {
                SpanText("Name: ", Modifier.margin(right = 28.px).fontWeight(FontWeight.Bold))
                Text(StoreUserSession.getRepresentativeName())
            }

            P(attrs = GenDefaultText.margin(bottom = 6.px).toAttrs()) {
                SpanText("Date: ", Modifier.margin(right = 40.px).fontWeight(FontWeight.Bold))
                Text(formData.repSignDate)
            }

            P(attrs = GenDefaultText.margin(bottom = 6.px).toAttrs()) {
                SpanText("Signature: ", Modifier.margin(right = 40.px).fontWeight(FontWeight.Bold))
                Img(
                    src = formData.repSignature,
                    alt = "",
                    attrs = Modifier
                        .width(200.px)
                        .height(autoLength)
                        .toAttrs()
                )
            }

            HorizontalDivider(GenHorizontalDivider.toModifier().margin(topBottom = 16.px))

            P(attrs = formDefaultText.fontStyle(FontStyle.Italic).margin(bottom = 16.px).toAttrs()) {
                Text("Thank you for supporting energy-efficient lighting through Green Rebates end the ETO Grow Light Fixture Program.")
            }
        }
    }
}

@Composable
fun AttestationStatementList(
    display: Boolean // Left in when this was needed dynamically.
) {
    if (display) {
        P(attrs = GenDefaultText.fontWeight(FontWeight.Bold).margin(bottom = 8.px).toAttrs()) {
            Text("Attestation")
        }

        P(attrs = formDefaultText.margin(bottom = 16.px).toAttrs()) {
            Text("By Signing below, the undersigned Store Representative attests and agrees to the following:")
        }

        Ol() {
            Li() {
                P(formDefaultText.toAttrs()) {
                    Text("The information provided in this report is accurate and complete to the best of their knowledge.")
                }
            }
            Li() {
                P(formDefaultText.toAttrs()) {
                    Text("All sales reported above were made to end-use customers during the active program period.")
                }
            }
            Li() {
                P(formDefaultText.toAttrs()) {
                    Text("The customer received the full benefit of the rebate at the point of sale, as reflected in the final promotional price.")
                }
            }
            Li() {
                P(formDefaultText.toAttrs()) {
                    Text("No more than ")
                    SpanText("two (2) ", Modifier.fontWeight(FontWeight.Bold))
                    Text("qualifying fixtures were sold per customer, per location, per day, in compliance wih program requirements.")
                }
            }
            Li() {
                P(formDefaultText.toAttrs()) {
                    Text("Only eligible products and SKUs listed on the current program schedule were promoted and invoiced.")
                }
            }
            Li() {
                P(formDefaultText.toAttrs()) {
                    Text("All products were sold at approved store locations listed in Attachment A of the program schedule.")
                }
            }
        }
    }
}

@Composable
fun AttestationPageNumber(
    pageNumber: Int,
    pageTotal: Int,
) {
    SpanText(
        text = "Page $pageNumber of $pageTotal",
        modifier = formDefaultText
            .fontSize(0.875.cssRem)
            .position(Position.Absolute)
            .top(1.cssRem)
            .right(1.cssRem)
            .fontWeight(400)
            .fontStyle(FontStyle.Italic)
    )
}